// Pesawat komersial (dipisah dari engine.js, perilaku identik): terbang → mendarat → berhenti → terbang.
import { TILE_H } from "./iso";
import { paintBuildingAt } from "./paint";

export function createPlane(c) {
  // c = { gridToScreen, cam, get atmos }
  const cam = c.cam, gridToScreen = c.gridToScreen;
  let ctx = null, atmos = null, runwayInfo = null, planeDefCache = null, W = 0, H = 0;

  // ================= PESAWAT KOMERSIAL: terbang → mendarat → berhenti → terbang =================
  // Satu pesawat berputar: mendekat dari KIRI (tinggi), turun mendarat di ujung
  // landasan, menggelinding melambat sampai BERHENTI di tengah landasan (2/4),
  // diam 10 detik, lalu menggelinding cepat ke ujung dan lepas landas naik.
  let plane = null;
  const planePos = {}; // posisi layar pesawat frame ini (debug/uji otomatis)
  const PLANE_STOP_T = 10; // detik berhenti di landasan
  const PLANE_SCALE = 1.3; // jet L=80 unit ×1.25 (skala mover) ×1.3 ≈ 1.8 tile — bentang sayap ≈ lebar landasan 2 tile
  function planeReset() {
    if (!runwayInfo) { plane = null; return; }
    plane = { phase: "approach", prog: 0, dist: 22, alt: 1, stop: 0 }; // start lebih jauh
  }
  // posisi sepanjang landasan (tile fraksional) → layar
  function planeScreen(s, altPx) {
    let gx, gy;
    if (runwayInfo.axis === "x") { gx = s; gy = runwayInfo.midC; }
    else { gx = runwayInfo.midC; gy = s; }
    const [x, y] = gridToScreen(gx, gy);
    return [x, y - altPx];
  }
  // smoothstep untuk perpindahan ketinggian yang HALUS (sudut miring, tidak kaku)
  function sstep(p) { p = Math.min(1, Math.max(0, p)); return p * p * (3 - 2 * p); }
  function drawPlane(t, dt) {
    if (!runwayInfo || !planeDefCache) return;
    if (!plane) planeReset();
    if (!plane) return;
    const z = cam.zoom;
    const hhPix = (TILE_H / 2) * z;
    // Arah travel pesawat = dari sisi SW layar (kiri-bawah) ke sisi NE (kanan-atas),
    // mengikuti panah START→FINISH. Sumbu-x: dari x kecil ke besar (lo→hi). Sumbu-y:
    // dari y besar ke kecil (hi→lo). start = ujung touchdown, end = ujung lepas landas.
    const fwd = runwayInfo.axis === "x";      // true: lo→hi ; false: hi→lo
    const startS = fwd ? runwayInfo.lo : runwayInfo.hi;
    const endS = fwd ? runwayInfo.hi : runwayInfo.lo;
    const stopS = (runwayInfo.lo + runwayInfo.hi) / 2; // titik berhenti = tengah (2/4)
    let s, altPx;
    // KETINGGIAN jelajah: pesawat yang sedang di udara melayang TINGGI di atas semua
    // bangunan (tidak tertutup kebun/gedung). Turun ke 0 hanya saat touchdown.
    const CRUISE = 30 * hhPix; // ketinggian jelajah (di atas gedung tertinggi)
    if (plane.phase === "approach") {
      // mendekat dari KIRI-BAWAH (di luar ujung start). Ketinggian menurun HALUS &
      // MIRING (lerp linier terhadap jarak, dismooth) dari CRUISE ke 0 di touchdown.
      plane.prog += dt / 9;
      const p = Math.min(1, plane.prog);
      const off = fwd ? -plane.dist : plane.dist; // di luar ujung start
      s = startS + off * (1 - p);
      altPx = (1 - sstep(p)) * CRUISE; // turun smooth membentuk sudut miring
      if (p >= 1) { plane.phase = "roll"; plane.prog = 0; }
    } else if (plane.phase === "roll") {
      // touchdown → menggelinding melambat ke titik tengah, lalu berhenti
      plane.prog += dt / 4;
      const p = Math.min(1, plane.prog);
      const e = 1 - Math.pow(1 - p, 2);
      s = startS + (stopS - startS) * e;
      altPx = 0;
      if (p >= 1) { plane.phase = "stop"; plane.prog = 0; plane.stop = 0; }
    } else if (plane.phase === "stop") {
      plane.stop += dt;
      s = stopS; altPx = 0;
      if (plane.stop >= PLANE_STOP_T) { plane.phase = "takeoffroll"; plane.prog = 0; }
    } else if (plane.phase === "takeoffroll") {
      // menggelinding cepat ke ujung akhir landasan
      plane.prog += dt / 3;
      const p = Math.min(1, plane.prog);
      const e = p * p; // percepat
      s = stopS + (endS - stopS) * e;
      altPx = 0;
      if (p >= 1) { plane.phase = "climb"; plane.prog = 0; }
    } else if (plane.phase === "climb") {
      // lepas landas: naik HALUS & MIRING ke CRUISE (lerp dismooth) sambil menjauh
      plane.prog += dt / 9;
      const p = Math.min(1, plane.prog);
      const off = fwd ? plane.dist * 0.8 : -plane.dist * 0.8;
      s = endS + off * p;
      altPx = sstep(p) * CRUISE; // naik smooth membentuk sudut miring
      if (p >= 1) { planeReset(); return; }
    }
    if (s == null) return;
    // arah hadap pesawat DIBALIK 180° agar hidung mengarah MAJU searah travel
    // (sebelumnya pesawat tampak berjalan mundur/ekor di depan).
    // sumbu-x: SE→NW ; sumbu-y: SW→NE.
    const dir = runwayInfo.axis === "x" ? "NW" : "NE";
    // bayangan HANYA saat pesawat menapak landasan (di udara: tanpa bayangan sama sekali)
    const [gx0, gy0] = runwayInfo.axis === "x" ? [s, runwayInfo.midC] : [runwayInfo.midC, s];
    const [gxs, gys] = gridToScreen(gx0, gy0);
    if (altPx < 0.5) {
      ctx.save();
      ctx.globalAlpha = 0.22;
      ctx.fillStyle = "#0f172a";
      ctx.beginPath();
      ctx.ellipse(gxs, gys, 30 * z, 12 * z, 0, 0, 7);
      ctx.fill();
      ctx.restore();
    }
    // posisi layar pesawat: aset gaya Jawsel berpijak di pusat (roda di z=0) sehingga
    // saat altPx≈0 kaki pesawat langsung menempel di permukaan landasan.
    const [px0, py0] = planeScreen(s, 0);
    const px = px0;
    const py = py0 - altPx;                            // alt mengangkat dari titik kaki
    planePos.phase = plane.phase; planePos.x = px; planePos.y = py; planePos.alt = altPx; planePos.gx = gx0; planePos.gy = gy0;
    if (px < -200 || px > W + 200 || py < -260 || py > H + 260) return;
    ctx.save();
    // pesawat diperbesar PLANE_SCALE x — footprint di-scale di sekitar titik pendaratan
    ctx.translate(px, py);
    ctx.scale(PLANE_SCALE, PLANE_SCALE);
    ctx.translate(-px, -py);
    paintBuildingAt(ctx, { ...planeDefCache, category: "Kendaraan" }, px, py, z,
      { time: t, night: atmos.dark, dir, noShadow: true });
    ctx.restore();
  }

  return {
    setRunway(info, def) { runwayInfo = info; planeDefCache = def; if (!info) plane = null; },
    draw(g, t, dt, w, h) { ctx = g; atmos = c.atmos; W = w; H = h; drawPlane(t, dt); },
    pos: planePos
  };
}
